import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import ContactUsModal from "../components/ContactUsModal";
import { parseLocalDate } from "@ui/shared";
import {
  ClaimSummary,
  ClaimDocumentSummary,
  ClaimInstallmentPlanStatus,
  ClaimInstallmentStatus,
  getClaim,
  getClaimDocuments,
  getClaimDocumentDownloadUrl,
  updatePlanPaymentMethod,
} from "../api/services";
import { PaymentMethodPicker } from "../components/payment";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { formatCurrency } from "../utils/formatCurrency";

const planStatusColor = (status: ClaimInstallmentPlanStatus) => {
  switch (status) {
    case "completed":
      return "text-success";
    case "failed":
    case "cancelled":
      return "text-danger";
    default:
      return "text-info";
  }
};

const installmentStatusColor = (status: ClaimInstallmentStatus) => {
  switch (status) {
    case "succeeded":
      return "text-success";
    case "failed":
    case "cancelled":
      return "text-danger";
    case "processing":
      return "text-info";
    default:
      return "text-muted";
  }
};

const claimStatusColor = (status: ClaimSummary["status"]) => {
  switch (status) {
    case "approved":
    case "paid":
    case "completed":
      return "text-success";
    case "denied":
    case "withdrawn":
    case "uncollectable":
      return "text-danger";
    case "processing":
    case "additional-info-required":
    case "disputed":
      return "text-info";
    default:
      return "text-primary";
  }
};

const policyStatusBadge = (
  status: "pending" | "covered" | "revoked" | "expired",
) => {
  switch (status) {
    case "covered":
      return "bg-success";
    case "pending":
      return "bg-info";
    case "expired":
      return "bg-gray-400";
    case "revoked":
      return "bg-danger";
    default:
      return "bg-gray-400";
  }
};
// Dual-purpose: callers feed DATE-only fields (policy.startsOn/endsOn)
// AND DATETIME fields (claim.createdAt, claim.installmentPlan.*).
// parseLocalDate detects YYYY-MM-DD and uses local-midnight parts; for
// datetime ISO strings it falls through to standard `new Date(s)` parse,
// which is the correct behavior — those are real instants.
const formatDate = (s: string | null | undefined): string =>
  s ? parseLocalDate(s).toLocaleDateString() : "—";

const ClaimDetails: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [claim, setClaim] = useState<ClaimSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  const [documents, setDocuments] = useState<ClaimDocumentSummary[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);

  // Inline "change payment method" flow for an active plan.
  const { methods, loading: methodsLoading } = usePaymentMethods();
  const [editingMethod, setEditingMethod] = useState(false);
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [savingMethod, setSavingMethod] = useState(false);
  const [methodError, setMethodError] = useState<string | null>(null);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    getClaim(uuid)
      .then((res) => {
        if (!cancelled) setClaim(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error("Failed to load claim:", err);
          setError(t("claimDetails.errorLoading"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uuid]);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    setDocumentsLoading(true);
    setDocumentsError(null);
    getClaimDocuments(uuid)
      .then((res) => {
        if (!cancelled) setDocuments(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status !== 404) {
          console.error("Failed to load claim documents:", err);
          setDocumentsError(t("claimDetails.documentsError"));
        }
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, t]);

  const handleDownload = async (documentUuid: string) => {
    if (!uuid) return;
    setDownloadingUuid(documentUuid);
    try {
      const res = await getClaimDocumentDownloadUrl(uuid, documentUuid);
      window.open(res.data.downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to download document:", err);
      setDocumentsError(t("claimDetails.documentsError"));
    } finally {
      setDownloadingUuid(null);
    }
  };

  const startEditingMethod = () => {
    setMethodError(null);
    // Preselect the plan's current method so the picker opens on it.
    setSelectedMethodId(claim?.installmentPlan?.paymentMethodId ?? null);
    setEditingMethod(true);
  };

  const cancelEditingMethod = () => {
    setEditingMethod(false);
    setMethodError(null);
  };

  const handleSaveMethod = async () => {
    if (!uuid || selectedMethodId == null) return;
    setSavingMethod(true);
    setMethodError(null);
    try {
      await updatePlanPaymentMethod(uuid, selectedMethodId);
      // Reload the claim so the displayed method + plan reflect the change.
      const res = await getClaim(uuid);
      setClaim(res.data);
      setEditingMethod(false);
    } catch (err) {
      console.error("Failed to update payment method:", err);
      setMethodError(t("claimDetails.methodUpdateError"));
    } finally {
      setSavingMethod(false);
    }
  };

  if (loading) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <p className="text-sm text-muted">{t("claimDetails.loading")}</p>
      </div>
    );
  }

  if (notFound || !claim) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error || t("claimDetails.notFound")}
        </div>
      </div>
    );
  }

  const policy = claim.policy;

  return (
    <div>
      <GoBack to="/dashboard" />

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {/* Policy Information */}
      {policy && (
        <div className="relative mb-6 overflow-hidden rounded-xl bg-white shadow-sm">
          <span
            className={`absolute right-4 top-4 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize text-white ${policyStatusBadge(policy.status)}`}
          >
            {policy.status}
          </span>

          <div className="p-5">
            <p className="text-base font-semibold text-primary">
              {policy.unit?.property?.name ?? ""} – {policy.unit?.name ?? ""}
            </p>

            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <p className="text-xs text-muted">{t("claimDetails.policyLabel")}</p>
                <p className="font-medium">{policy.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted">{t("claimDetails.coverage")}</p>
                <p className="font-medium">
                  {policy.coverageAmount !== null
                    ? formatCurrency(policy.coverageAmount)
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">{t("claimDetails.effective")}</p>
                <p className="font-medium">
                  {formatDate(policy.startsOn)} – {formatDate(policy.endsOn)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">{t("claimDetails.address")}</p>
                <p className="font-medium">
                  {policy.unit?.property?.address ?? "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Details */}
      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h3 className="mb-4 text-base font-semibold text-primary">
            {t("claimDetails.sectionTitle")}
          </h3>

          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <div>
              <p className="text-xs text-muted">{t("claimDetails.status")}</p>
              <p
                className={`font-semibold ${claimStatusColor(claim.status)}`}
              >
                {t(`claimStatusLabel.${claim.status}`, claim.status)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted">{t("claimDetails.type")}</p>
              <p className="font-medium">{t(`claimType.${claim.type}`, claim.type)}</p>
            </div>
            <div>
              <p className="text-xs text-muted">{t("claimDetails.requestedAmount")}</p>
              <p className="font-semibold text-primary">
                {formatCurrency(claim.requestAmount)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted">{t("claimDetails.submitted")}</p>
              <p className="font-medium">{formatDate(claim.createdAt)}</p>
            </div>
          </div>

        </div>
      </div>

      {/* Payment Plan */}
      {claim.installmentPlan && (
        <div className="mb-6 rounded-xl bg-white shadow-sm">
          <div className="p-5">
            <h3 className="mb-4 text-base font-semibold text-primary">
              {t("claimDetails.planTitle")}
            </h3>

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <p className="text-xs text-muted">
                  {t("claimDetails.planType")}
                </p>
                <p className="font-medium">
                  {t(
                    `claimDetails.planTypeLabels.${claim.installmentPlan.planType}`,
                    claim.installmentPlan.planType,
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">
                  {t("claimDetails.planStatus")}
                </p>
                <p
                  className={`font-semibold ${planStatusColor(claim.installmentPlan.status)}`}
                >
                  {t(
                    `claimDetails.planStatusLabels.${claim.installmentPlan.status}`,
                    claim.installmentPlan.status,
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">
                  {t("claimDetails.planNextDue")}
                </p>
                <p className="font-medium">
                  {formatDate(claim.installmentPlan.nextDueDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">
                  {t("claimDetails.planTotal")}
                </p>
                <p className="font-medium">
                  {formatCurrency(claim.installmentPlan.totalAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">
                  {t("claimDetails.planPaid")}
                </p>
                <p className="font-semibold text-success">
                  {formatCurrency(claim.installmentPlan.paidAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted">
                  {t("claimDetails.planRemaining")}
                </p>
                <p className="font-semibold text-primary">
                  {formatCurrency(claim.installmentPlan.remainingAmount)}
                </p>
              </div>
            </div>

            {/* Progress bar — paid vs total. */}
            {(() => {
              const total = claim.installmentPlan.totalAmount;
              const paid = claim.installmentPlan.paidAmount;
              const percent = Math.min(
                100,
                Math.max(0, total > 0 ? Math.round((paid / total) * 100) : 0),
              );
              return (
                <div className="mt-4">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-green-500 transition-all"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    {t("claimDetails.planProgress", { percent })}
                  </p>
                </div>
              );
            })()}

            {/* Change payment method — active plans only. */}
            {claim.installmentPlan.status === "active" && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                    {t("claimDetails.paymentMethodTitle")}
                  </p>
                  {!editingMethod && (
                    <button
                      type="button"
                      className="cursor-pointer text-sm font-medium text-info underline hover:text-sky-600"
                      onClick={startEditingMethod}
                    >
                      {t("claimDetails.changeMethod")}
                    </button>
                  )}
                </div>

                {methodError && (
                  <div className="mt-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
                    {methodError}
                  </div>
                )}

                {editingMethod ? (
                  <div className="mt-3">
                    {methodsLoading ? (
                      <p className="text-sm text-muted">
                        {t("claimDetails.methodsLoading")}
                      </p>
                    ) : (
                      <PaymentMethodPicker
                        methods={methods}
                        selectedId={selectedMethodId}
                        onSelect={setSelectedMethodId}
                        emptyMessage={t("claimDetails.methodsEmpty")}
                      />
                    )}
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                        onClick={cancelEditingMethod}
                        disabled={savingMethod}
                      >
                        {t("claimDetails.methodCancel")}
                      </button>
                      <button
                        type="button"
                        className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                        onClick={handleSaveMethod}
                        disabled={
                          savingMethod ||
                          selectedMethodId == null ||
                          selectedMethodId ===
                            claim.installmentPlan.paymentMethodId
                        }
                      >
                        {savingMethod
                          ? t("claimDetails.methodSaving")
                          : t("claimDetails.methodSave")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-sm font-medium text-gray-800">
                    {(() => {
                      // Prefer the plan's own method snapshot (always present,
                      // even if the method was since removed); fall back to
                      // the active-methods list only if absent.
                      const current =
                        claim.installmentPlan!.paymentMethod ??
                        methods.find(
                          (m) =>
                            m.id === claim.installmentPlan!.paymentMethodId,
                        );
                      if (!current) return t("claimDetails.methodCurrentUnknown");
                      const isCard = current.type === "card";
                      const label = isCard
                        ? current.brand || t("paymentMethodPicker.card")
                        : current.bankName ||
                          t("paymentMethodPicker.bankAccount");
                      return `${label} •••• ${current.last4}`;
                    })()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Installment History */}
      {claim.installmentPlan &&
        claim.installmentPlan.installmentsList.length > 0 && (
          <div className="mb-6 rounded-xl bg-white shadow-sm">
            <div className="p-5">
              <h3 className="mb-4 text-base font-semibold text-primary">
                {t("claimDetails.planHistoryTitle")}
              </h3>

              <ul className="divide-y divide-gray-100">
                {claim.installmentPlan.installmentsList.map((inst) => (
                  <li
                    key={inst.uuid}
                    className="flex items-center justify-between py-3 text-sm"
                  >
                    <div>
                      <p className="font-medium text-gray-800">
                        {t("claimDetails.planInstallmentNumber", {
                          n: inst.sequence,
                          total: claim.installmentPlan!.installments,
                        })}
                      </p>
                      <p className="text-xs text-muted">
                        {t("claimDetails.planDueOn", {
                          date: formatDate(inst.dueDate),
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">
                        {formatCurrency(inst.amount)}
                      </p>
                      <p
                        className={`text-xs font-medium ${installmentStatusColor(inst.status)}`}
                      >
                        {t(
                          `claimDetails.installmentStatusLabels.${inst.status}`,
                          inst.status,
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

      {/* Supporting Documents */}
      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h3 className="mb-4 text-base font-semibold text-primary">
            {t("claimDetails.documentsTitle")}
          </h3>

          {documentsError && (
            <div className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {documentsError}
            </div>
          )}

          {documentsLoading ? (
            <p className="text-sm text-muted">
              {t("claimDetails.documentsLoading")}
            </p>
          ) : documents.length === 0 ? (
            <p className="text-sm text-muted">
              {t("claimDetails.documentsEmpty")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {documents.map((doc) => {
                const busy = downloadingUuid === doc.uuid;
                return (
                  <button
                    key={doc.uuid}
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-info underline hover:text-sky-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                    onClick={() => handleDownload(doc.uuid)}
                    disabled={busy}
                  >
                    <svg
                      className="h-4 w-4 shrink-0 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    {busy
                      ? t("claimDetails.downloading")
                      : doc.documentTypeTitle || doc.fileName}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h3 className="mb-4 text-base font-semibold text-primary">
            {t("claimDetails.actions")}
          </h3>

          <div className="flex flex-col gap-3 sm:flex-row">
            {(!claim.installmentPlan ||
              claim.installmentPlan.status === "failed" ||
              claim.installmentPlan.status === "cancelled") &&
              claim.status !== "paid" && (
                <button
                  className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
                  onClick={() => navigate(`/claim/${claim.uuid}/pay`)}
                >
                  {t("claimDetails.payClaim")}
                </button>
              )}
            <button
              className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
              onClick={() => setContactOpen(true)}
            >
              {t("claimDetails.contactUs")}
            </button>
          </div>

          {claim.status === "submitted" && (
            <div className="mt-4 rounded-lg bg-sky-50 px-4 py-3 text-sm text-info">
              <p className="mb-1 font-medium">
                {t("claimDetails.whatHappensNext.title")}
              </p>
              <p>{t("claimDetails.whatHappensNext.body")}</p>
            </div>
          )}
        </div>
      </div>

      <ContactUsModal show={contactOpen} onClose={() => setContactOpen(false)} />

    </div>
  );
};

export default ClaimDetails;
